import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { components } from '../../api/schema'
import { getLocalizedName } from '../../lib/localization'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

export type Listing = components['schemas']['Listing']

export const CURRENCIES = ['EUR', 'GBP', 'CHF', 'USD'] as const

/** The label of a contract type: the API sends `names`, older callers `name`; the code when neither resolves. */
export function contractTypeLabel(ct: { code: string; names?: unknown; name?: unknown }, locale: string): string {
    const raw = ct.names ?? ct.name
    const label = typeof raw === 'string' ? raw : getLocalizedName(raw as Record<string, string> | undefined, locale)
    return label || ct.code
}

const STATUS_VARIANT: Record<string, 'success' | 'secondary' | 'warning' | 'outline' | 'destructive'> = {
    draft: 'secondary',
    published: 'success',
    paused: 'warning',
    let: 'outline',
    withdrawn: 'destructive',
}

export function ListingStatusBadge({ status, testId }: { status: string; testId?: string }) {
    const { t } = useTranslation('listings')
    return (
        <Badge variant={STATUS_VARIANT[status] ?? 'secondary'} data-testid={testId} data-status={status}>
            {t(`status.${status}`, { defaultValue: status })}
        </Badge>
    )
}

/** Refetch every listings query after a write. */
export function useRefreshListings() {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (q) => {
                const [method, path] = q.queryKey as [string, string]
                return method === 'get' && typeof path === 'string' && path.startsWith('/api/v1/organizations/{orgCode}/listings')
            },
        })
}

export function CurrencySelect({ value, onChange, disabled, testId }: { value: string; onChange: (v: string) => void; disabled?: boolean; testId: string }) {
    const options = CURRENCIES.includes(value as (typeof CURRENCIES)[number]) || !value ? CURRENCIES : [...CURRENCIES, value]
    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={testId}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {options.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`${testId}-${c}`}>
                        {c}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

/** Major units typed by a person to minor units stored (two decimals). */
export const toMinor = (text: string): number | null => {
    const n = Number(text.replace(',', '.'))
    return text.trim() === '' || Number.isNaN(n) ? null : Math.round(n * 100)
}
export const fromMinor = (minor: number | null | undefined): string => (minor == null ? '' : String(minor / 100))
